import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fileService,
  FileInfo,
  FileMetadata,
  FileSearchFilters,
} from '../services/fileService';

const DEBOUNCE_MS = 300;

const EMPTY_METADATA: FileMetadata = { categories: [], tags: [], fileTypes: [] };

export interface FileSearchState {
  query: string;
  setQuery: (query: string) => void;
  files: FileInfo[];
  metadata: FileMetadata;
  isLoading: boolean;
  filters: FileSearchFilters;
  toggleFilter: (type: keyof FileSearchFilters, value: string) => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
}

/**
 * Debounced file search behind the chat's context picker. Responses are matched
 * against a request counter so a slow early query cannot overwrite a later
 * one's results — typing fast otherwise leaves the list showing a stale prefix.
 */
export function useFileSearch(): FileSearchState {
  const [query, setQuery] = useState('');
  const [files, setFiles] = useState<FileInfo[]>([]);
  const [metadata, setMetadata] = useState<FileMetadata>(EMPTY_METADATA);
  const [filters, setFilters] = useState<FileSearchFilters>({});
  const [isLoading, setIsLoading] = useState(false);
  const requestCounterRef = useRef(0);

  useEffect(() => {
    fileService.getFileMetadata().then(setMetadata).catch(() => setMetadata(EMPTY_METADATA));
  }, []);

  const loadFiles = useCallback(async (search: string, active: FileSearchFilters) => {
    const request = ++requestCounterRef.current;
    setIsLoading(true);
    try {
      const results = search.trim()
        ? await fileService.searchFiles(search, 20, active)
        : await fileService.getFilesForSelection(active);
      if (request === requestCounterRef.current) setFiles(results);
    } catch (error) {
      console.error('Failed to load files:', error);
      if (request === requestCounterRef.current) setFiles([]);
    } finally {
      if (request === requestCounterRef.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => loadFiles(query, filters), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, filters, loadFiles]);

  const toggleFilter = useCallback((type: keyof FileSearchFilters, value: string) => {
    setFilters((prev) => {
      const current = prev[type] || [];
      return {
        ...prev,
        [type]: current.includes(value)
          ? current.filter((entry) => entry !== value)
          : [...current, value],
      };
    });
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({});
    setQuery('');
  }, []);

  return {
    query,
    setQuery,
    files,
    metadata,
    isLoading,
    filters,
    toggleFilter,
    clearFilters,
    hasActiveFilters: Object.values(filters).some((value) => value && value.length > 0),
  };
}
